import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  apiRecoveryReloadOnCooldown,
  isServerUnreachable,
  markApiRecoveryReload,
  probeApiHealth,
  subscribeServerReachability,
} from "@shared/serverReachability";

export type ServerUnavailableLocale = "de" | "en";

const COPY = {
  de: {
    title: "Server nicht erreichbar",
    message:
      "Der Server ist aktuell nicht erreichbar. Die Seite wird automatisch neu geladen, sobald die Verbindung wieder steht.",
    retry: "Erneut versuchen",
    checking: "Verbindung wird geprüft…",
    stillDown: "Der Server ist weiterhin nicht erreichbar.",
    backOnline: "Der Server antwortet wieder. Die Seite kann neu geladen werden.",
  },
  en: {
    title: "Server unavailable",
    message:
      "The server is currently unavailable. This page reloads automatically once the connection is back.",
    retry: "Try again",
    checking: "Checking the connection…",
    stillDown: "The server is still unavailable.",
    backOnline: "The server is responding again. You can reload the page.",
  },
} as const;

type Notice = "still-down" | "back-online" | null;

export function ServerUnavailableOverlay({
  apiBase,
  locale = "de",
}: {
  apiBase: string;
  locale?: ServerUnavailableLocale;
}) {
  const copy = COPY[locale];
  const titleId = useId();
  const messageId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const probeRef = useRef<(manual: boolean) => Promise<void>>(async () => {});
  const openRef = useRef(isServerUnreachable());
  const [open, setOpen] = useState(isServerUnreachable);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  useEffect(() => {
    return subscribeServerReachability((value) => {
      const wasOpen = openRef.current;
      if (wasOpen && !value) {
        if (apiRecoveryReloadOnCooldown()) {
          setNotice("back-online");
          return;
        }
        markApiRecoveryReload();
        window.location.reload();
        return;
      }
      openRef.current = value;
      setOpen(value);
    });
  }, []);

  useEffect(() => {
    if (open) return;
    setBusy(false);
    setNotice(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const root = document.getElementById("root");
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (root) root.inert = true;
    buttonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (root) root.inert = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    let current = 0;

    const probe = async (manual: boolean) => {
      const token = ++current;
      if (manual) setBusy(true);
      const ok = await probeApiHealth(apiBase);
      if (!active || token !== current) return;
      if (manual) setBusy(false);
      if (!ok) {
        if (manual) setNotice("still-down");
        return;
      }
      if (manual || !apiRecoveryReloadOnCooldown()) {
        markApiRecoveryReload();
        window.location.reload();
        return;
      }
      setNotice("back-online");
    };

    probeRef.current = probe;
    void probe(false);
    const timer = window.setInterval(() => {
      void probe(false);
    }, 5000);

    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [open, apiBase]);

  if (!open || typeof document === "undefined") return null;

  const statusText = busy
    ? copy.checking
    : notice === "still-down"
      ? copy.stillDown
      : notice === "back-online"
        ? copy.backOnline
        : copy.checking;

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={messageId}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding:
          "max(1.5rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) max(1.5rem, env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
        background: "var(--eh-bg-grouped, var(--bk-cream-50, #f2f2f7))",
        color: "var(--eh-label, #111827)",
        fontFamily: "inherit",
      }}
    >
      <div style={{ width: "min(100%, 28rem)", textAlign: "center" }}>
        <div
          aria-hidden
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "4.5rem",
            height: "4.5rem",
            marginBottom: "1.25rem",
            borderRadius: "1.25rem",
            background: "color-mix(in srgb, var(--eh-red, #ff3b30) 14%, var(--eh-bg-card, #fff))",
            color: "var(--eh-red, #ff3b30)",
          }}
        >
          <ServerOffIcon />
        </div>
        <h1
          id={titleId}
          style={{ margin: 0, fontSize: "1.75rem", lineHeight: 1.2, fontWeight: 600, letterSpacing: "-0.02em" }}
        >
          {copy.title}
        </h1>
        <p
          id={messageId}
          style={{
            margin: "0.875rem 0 0",
            fontSize: "1.0625rem",
            lineHeight: 1.5,
            color: "var(--eh-label-secondary, #4b5563)",
          }}
        >
          {copy.message}
        </p>
        <p aria-live="polite" style={{ margin: "1rem 0 0", minHeight: "1.5rem", fontSize: "0.9375rem" }}>
          {statusText}
        </p>
        <button
          ref={buttonRef}
          type="button"
          disabled={busy}
          aria-busy={busy || undefined}
          onClick={() => void probeRef.current(true)}
          style={{
            marginTop: "1.25rem",
            minWidth: "12rem",
            height: "2.75rem",
            padding: "0 1.25rem",
            border: "none",
            borderRadius: "0.75rem",
            background: "var(--eh-accent, #007aff)",
            color: "var(--eh-label-on-accent, #fff)",
            font: "inherit",
            fontSize: "1rem",
            fontWeight: 600,
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.65 : 1,
          }}
        >
          {busy ? copy.checking : copy.retry}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function ServerOffIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 7.5h14M5 12h8M5 16.5h5M4 4.5h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z"
      />
      <path strokeLinecap="round" d="M4 20.5 20 3.5" />
    </svg>
  );
}
